import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import '../../../css/Pages.css';
// import custom hooks
import UseMessageSelection from '../UseMessageSelection';
import useMessages from '../UseMessages';
// import custome logic
import FileDownloadService from '../FileDownloadService';
import { hasDownloadPermission, hasRetryPermission, isMessageTooOld } from '../DashboardUtilities';
// import sub components
import FilterBar from './Filterbar';
import ActionBar from './ActionBar';
import MessageRow from './MessageRow';
import PaginationControls from './PaginationControls';
import Notification from './Notification';
import RenderOnRole from "../../../Utilities/RenderOnRole";
import FieldSelector from './FieldSelector';
import ExportMsgBtn from './ExportMsgsBtn';

const BerichtDownloadComponent = ({
  interface: interfaceConfig,
  abonnementId,
  customerDebiNr,
  axiosInstance,
}) => {
  const { t } = useTranslation();
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(50);
  const [berichtDisplayFieldDict, setBerichtDisplayFieldDict] = useState([
    { id: "type", label: t('messageDownload.type'), sortFieldName: "Type", active: true, employeeOnly: false, hasCopyBtn: false },
    { id: "displayNaamVerzend", label: t('interface.sender'), sortFieldName: "ZenderNaam", active: true, employeeOnly: false, hasCopyBtn: false },
    { id: "glN_zender", label: t('messageDownload.scsnNumber'), sortFieldName: "ZenderGLN", active: false, employeeOnly: false, hasCopyBtn: true },
    { id: "displayNaamOntvangt", label: t('interface.receiver'), sortFieldName: "OntvangerNaam", active: true, employeeOnly: false, hasCopyBtn: false },
    { id: "glN_ontvanger", label: t('messageDownload.scsnNumber'), sortFieldName: "OntvangerGLN", active: false, employeeOnly: false, hasCopyBtn: true },
    { id: "id", label: t('messageDownload.berichtIdLabel'), sortFieldName: "ID", active: true, employeeOnly: true, hasCopyBtn: true },
    { id: "functioneelID", label: t('messageDownload.functioneelIdLabel'), sortFieldName: "functioneelID", active: true, employeeOnly: false, hasCopyBtn: true },
    { id: "status", label: t('subscriptions.status'), sortFieldName: "status", active: true, employeeOnly: false, hasCopyBtn: false },
    { id: "verzonden_datumtijd", label: t('messageDownload.received'), sortFieldName: "VerzondenDatumtijd", active: false, employeeOnly: false, hasCopyBtn: false },
    { id: "ontvangen_datumtijd", label: t('messageDownload.lastUpdate'), sortFieldName: "OntvangenDatumtijd", active: true, employeeOnly: false, hasCopyBtn: false },
    { id: "agentname", label: t('messageDownload.agent'), sortFieldName: "agentname", active: false, employeeOnly: true, hasCopyBtn: false }
  ]);

  const scopeFilters = useMemo(() => {
    if (interfaceConfig?.id) {
      return {
        interfaceId: interfaceConfig.id,
        abonnementId: '',
        debiNr: '',
      };
    }

    if (abonnementId) {
      return {
        interfaceId: '',
        abonnementId,
        debiNr: '',
      };
    }

    return {
      interfaceId: '',
      abonnementId: '',
      debiNr: customerDebiNr || '',
    };
  }, [interfaceConfig?.id, abonnementId, customerDebiNr]);

  const [filters, setFilters] = useState({
    ...scopeFilters,
    startDate: '',
    endDate: '',
    ontvangerGLN: '',
    verzenderGLN: '',
    ontvangerRelaNr: '',
    verzenderRelaNr: '',
    functioneelId: '',
    panelBerichtId: '',
    status: '',
    pageNumber: 0,
    pageCount: 50,
  });

  const [sorting, setSorting] = useState({
    field: 'OntvangenDatumtijd',
    direction: 'Descending',
  });

  // Keep the current scope in sync with the dashboard selection level
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      ...scopeFilters,
      pageNumber: 0,
    }));
    setPageNumber(0);
  }, [scopeFilters]);

  // Update filters when pagination changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      pageNumber,
      pageCount,
    }));
  }, [pageNumber, pageCount]);

  const { messages, loading, error, totalCount, relatiesVerzend, relatiesOntvangt } = useMessages(filters, sorting, axiosInstance);
  const { selectedIds, toggleSelection, selectAll, clearSelection, isSelected } = UseMessageSelection();

  const [notifications, setNotifications] = useState([]);

  const selectedMessages = useMemo(() =>
    messages.filter(m => selectedIds.has(m.id)),
    [messages, selectedIds]
  );

  const hasOldMessages = useMemo(() =>
    selectedMessages.some(m => isMessageTooOld(m.verzonden_datumtijd, m.interface_id)),
    [selectedMessages]
  );

  const handleDownload = useCallback(async () => {
    if (!axiosInstance) return;

    const results = [];

    for (const message of selectedMessages) {
      try {
        const url = `/Berichten/Download?berichtId=${message.id}`;
        const response = await axiosInstance.get(url);

        if (response.status === 200) {
          for (const item of response.data) {
            const data = item.messageText;
            const extension = item.fileExtension;
            const tag = item.tag;

            let fileName = tag ? `${tag}_` : '';
            fileName += message.functioneelID || `${message.verzend_rela_naam}_naar_${message.ontvangt_rela_naam}_${message.verzonden_datumtijd}`;

            FileDownloadService.downloadFile(data, fileName, extension);
          }

          results.push({
            functioneelId: message.functioneelID,
            success: true,
            status: t('messageDownload.downloadSuccess')
          });
        }
      } catch (err) {
        results.push({
          functioneelId: message.functioneelID,
          success: false,
          status: err.response?.data || err.message
        });
      }
    }

    setNotifications(results);
    clearSelection();
  }, [selectedMessages, clearSelection, axiosInstance, t]);

  const handleRetry = useCallback(async (fileContent = null) => {
    if (!axiosInstance) return [];

    const results = [];
    let messagesToRetry = selectedMessages;

    // If fileContent provided but no messages selected, create a dummy message
    if (fileContent && messagesToRetry.length === 0) {
      messagesToRetry = [{ connectmessageId: 0, id: 0, functioneelID: t('messageDownload.newMessage') }];
    }

    for (const message of messagesToRetry) {
      try {
        const url = `/Berichten/Retry?InterfaceId=${interfaceConfig?.id ?? 0}&ConnectmessageId=${message.connectmessageId}&berichtId=${message.id}`;
        const body = JSON.stringify({ message: fileContent || '' });

        await axiosInstance.post(url, body, {
          headers: { 'Content-Type': 'application/json' }
        });

        results.push({
          functioneelId: message.functioneelID,
          success: true,
          status: t('messageDownload.retrySuccess')
        });
      } catch (err) {
        results.push({
          functioneelId: message.functioneelID,
          success: false,
          status: typeof err.response?.data === 'string'
            ? err.response.data
            : (err.response?.data?.message || err.message || t('messageDownload.retryErrorNoResponse'))
        });
      }
    }

    setNotifications(results);
    clearSelection();
    return results;
  }, [selectedMessages, clearSelection, axiosInstance, interfaceConfig?.id, t]);

  const handleRetryWithCorrection = useCallback(async (file) => {
    if (!file) {
      setNotifications([{
        functioneelId: 'N/A',
        success: false,
        status: t('messageDownload.noFileSelected')
      }]);
      return;
    }

    try {
      const content = await file.text();
      return await handleRetry(content);
    } catch (err) {
      setNotifications([{
        functioneelId: file.name,
        success: false,
        status: err.message
      }]);
      return [{
        functioneelId: file.name,
        success: false,
        status: err.message
      }];
    }
  }, [handleRetry, t]);

  const handleSortChange = (field) => {
    let actualField = field;

    // Check if interface sender is Stichting SCSN
    const isSenderSCSN = interfaceConfig?.verzenderNaam?.includes('Stichting SCSN');
    // Check if interface receiver is Stichting SCSN
    const isReceiverSCSN = interfaceConfig?.ontvangerNaam?.includes('Stichting SCSN');

    // Adjust field name only for the party that is SCSN
    if (field === 'ZenderNaam' && isSenderSCSN) {
      actualField = 'ZenderNaamSCSN';
    }
    else if (field === 'OntvangerNaam' && isReceiverSCSN) {
      actualField = 'OntvangerNaamSCSN';
    }

    setSorting(prev => ({
      field: actualField,
      direction: prev.field === actualField && prev.direction === 'Ascending' ? 'Descending' : 'Ascending',
    }));
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageCount(newPageSize);
    setPageNumber(0); // Reset to first page (0-based) when changing page size
  };

  const handleResetFilters = useCallback(() => {
    setFilters({
      ...scopeFilters,
      startDate: '',
      endDate: '',
      ontvangerGLN: '',
      verzenderGLN: '',
      ontvangerRelaNr: '',
      verzenderRelaNr: '',
      functioneelId: '',
      panelBerichtId: '',
      status: '',
      pageNumber: 0,
      pageCount: pageCount,
    });
    setPageNumber(0);
  }, [scopeFilters, pageCount]);

  const handleRefreshMessages = useCallback(() => {
    // Force a refetch by updating filters object reference
    setFilters(prev => ({ ...prev }));
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === messages.length && messages.length > 0) {
      clearSelection();
    } else {
      selectAll(messages);
    }
  }, [messages, selectedIds.size, selectAll, clearSelection]);

  const allSelected = useMemo(() =>
    messages.length > 0 && selectedIds.size === messages.length,
    [messages.length, selectedIds.size]
  );

  // Check user permissions
  const canDownload = useMemo(() => hasDownloadPermission(), []);
  const canRetry = useMemo(() => hasRetryPermission(), []);

  return (
    <div className="p-6 max-w-full">
      <div className="justify-between flex items-row">
        <h2 className="text-3xl font-bold mb-6 text-gray-800">{t('dashboard.messages')}</h2>

        <FieldSelector
          fields={berichtDisplayFieldDict}
          onChange={(newDict) => {
            setBerichtDisplayFieldDict(structuredClone(newDict));
          }}
        />
      </div>

      <FilterBar
        filters={filters}
        onFilterChange={setFilters}
        onResetFilters={handleResetFilters}
        relatiesVerzend={relatiesVerzend}
        relatiesOntvangt={relatiesOntvangt}
      />

      <div className="justify-between flex items-row, items-center">
        <ActionBar
          onDownload={handleDownload}
          onRetry={handleRetry}
          onRetryWithCorrection={handleRetryWithCorrection}
          onRefresh={handleRefreshMessages}
          selectedCount={selectedIds.size}
          hasListener={interfaceConfig?.listener || true}
          hasOldMessages={hasOldMessages}
          canDownload={canDownload}
          canRetry={canRetry}
        />
        <ExportMsgBtn
          messages={selectedMessages.length <= 0 ? messages : selectedMessages}
          berichtDisplayFieldDict={berichtDisplayFieldDict}
        />
      </div>


      {error && (
        <div className="error-alert">
          <h3>{t('messageDownload.errorLoadingMessages')}</h3>
          <p>{error}</p>
        </div>
      )}

      <PaginationControls
        currentPage={pageNumber}
        pageSize={pageCount}
        totalRecords={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <div className="table-wrapper">
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th className="w-12">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 cursor-pointer"
                    title={t('messageDownload.selectAll')}
                  />
                </th>
                {berichtDisplayFieldDict.map((berichtDisplayInfo) => {
                  if(!berichtDisplayInfo.active) return null;
                  else if(berichtDisplayInfo.employeeOnly) return (
                    <RenderOnRole
                      key={berichtDisplayInfo.id}
                      roles={["Administrator", "SD_Medewerker"]}
                    >
                      <th
                        className="cursor-pointer"
                        onClick={() => {if(berichtDisplayInfo.sortFieldName !== "agentname") {handleSortChange(berichtDisplayInfo.sortFieldName);}}}
                      >
                        <div className="flex items-center gap-1">
                          {berichtDisplayInfo.label}
                          {sorting.field === berichtDisplayInfo.sortFieldName && (
                            <span className="sort-indicator">
                              {sorting.direction === "Ascending" ? "↑" : "↓"}
                            </span>
                          )}
                        </div>
                      </th>
                    </RenderOnRole>
                  );
                  else return (
                    <th
                      key={berichtDisplayInfo.id}
                      className="cursor-pointer"
                      onClick={() => handleSortChange(berichtDisplayInfo.sortFieldName)}
                    >
                      <div className="flex items-center gap-1">
                        {berichtDisplayInfo.label}
                        {sorting.field === berichtDisplayInfo.sortFieldName && (
                          <span className="sort-indicator">
                            {sorting.direction === "Ascending" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    </th>
                  );}
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="text-center text-gray-500 py-12">
                    {t('messageDownload.loadingMessages')}
                  </td>
                </tr>
              ) : messages.length > 0 ? (
                messages.map((message, index) => (
                  <MessageRow
                    key={message.id}
                    message={message}
                    index={index}
                    isSelected={isSelected(message.id)}
                    onToggle={(id, idx, isShift) => toggleSelection(id, idx, isShift, messages)}
                    berichtDisplayFieldDict={berichtDisplayFieldDict}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center text-gray-500 py-12">
                    {t('messageDownload.noMessages')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Notification
        messages={notifications}
        onClose={() => setNotifications([])}
      />
    </div>
  );
};

export default BerichtDownloadComponent;